import { ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { GhostTrace } from './GhostTrace';

const THINKING_CAP_LABELS: { emoji: string; text: string }[] = [
  { emoji: '🧢', text: 'Thinking cap on' },
  { emoji: '🔮', text: 'Reading the tea leaves' },
  { emoji: '🧪', text: 'Cooking something up' },
  { emoji: '🎯', text: 'Locking in' },
  { emoji: '🧠', text: 'Big brain time' },
  { emoji: '🛠️', text: 'Under the hood' },
  { emoji: '🎰', text: 'Pulling levers' },
  { emoji: '🏄', text: 'Surfing the data' },
  { emoji: '🔬', text: 'Zooming in' },
  { emoji: '🎸', text: 'Riffing on it' },
  { emoji: '🚀', text: 'Firing thrusters' },
  { emoji: '🕵️', text: 'On the case' },
  { emoji: '⚡', text: 'Sparks flying' },
  { emoji: '🧩', text: 'Piecing it together' },
  { emoji: '🎧', text: 'In the zone' },
  { emoji: '🌀', text: 'Spiraling in' },
  { emoji: '🏗️', text: 'Building it out' },
  { emoji: '🎬', text: 'Lights, camera, action' },
  { emoji: '🧲', text: 'Pulling it together' },
  { emoji: '🦾', text: 'Flexing on this' },
  { emoji: '🪄', text: 'Working some magic' },
  { emoji: '📡', text: 'Tuning in' },
  { emoji: '🎪', text: 'Running the show' },
  { emoji: '🔥', text: 'Heating up' },
  { emoji: '🧭', text: 'Finding the way' },
  { emoji: '🎲', text: 'Rolling with it' },
  { emoji: '🌊', text: 'Riding the wave' },
  { emoji: '💡', text: 'Lightbulb moment' },
  { emoji: '🛸', text: 'Beaming down answers' },
  { emoji: '🎤', text: 'Dropping knowledge' },
];

export interface ThinkingCapTrace {
  id: string;
  verb: string;
  description?: string;
  input?: string;
  output?: string;
  loading: boolean;
  success: boolean;
  error?: boolean;
}

export interface ThinkingCapProps {
  traces: ThinkingCapTrace[];
  /** true while tool calls are still arriving (live streaming), false on replay/completion */
  active: boolean;
  className?: string;
}

function CapDot({ active }: { active: boolean }) {
  if (active) {
    return (
      <span
        role="img"
        className="w-1.5 h-1.5 rounded-full bg-sd-warning/60 shrink-0 sd-breathe"
        aria-label="Working"
      />
    );
  }
  return (
    <span
      role="img"
      className="w-1.5 h-1.5 rounded-full bg-sd-success/50 shrink-0"
      aria-label="Complete"
    />
  );
}

export function ThinkingCap({ traces, active, className = '' }: ThinkingCapProps) {
  const [expanded, setExpanded] = useState(false);
  const prevTraceCountRef = useRef(traces.length);
  const [tickerKey, setTickerKey] = useState(0);

  // Stable random label per component instance — won't change on re-renders
  const label = useMemo(
    () => THINKING_CAP_LABELS[Math.floor(Math.random() * THINKING_CAP_LABELS.length)]!,
    [],
  );

  // Bump ticker key when a new trace arrives to trigger fade animation
  useEffect(() => {
    if (traces.length > prevTraceCountRef.current) {
      setTickerKey((k) => k + 1);
    }
    prevTraceCountRef.current = traces.length;
  }, [traces.length]);

  if (traces.length === 0) return null;

  const lastTrace = traces[traces.length - 1]!;
  const tickerText = active
    ? `${lastTrace.verb}${lastTrace.description ? ` — ${lastTrace.description}` : ''}`
    : `✓ ${traces.length} operation${traces.length !== 1 ? 's' : ''}`;

  const toggle = () => setExpanded((prev) => !prev);

  return (
    <div
      data-testid="thinking-cap"
      className={`sd-fade-in ${className}`.trim()}
    >
      {/* Collapsed header — always visible */}
      <div
        role="button"
        aria-expanded={expanded}
        aria-label={`Thinking cap — ${tickerText}`}
        tabIndex={0}
        className="ml-10 pl-4 py-1 flex items-center gap-2 cursor-pointer group/cap select-none"
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <CapDot active={active} />

        {active ? (
          <>
            {/* Label — only shown while streaming */}
            <span data-testid="thinking-cap-label" className="text-[12px] text-sd-text-muted/50 leading-snug shrink-0">
              {label.emoji} {label.text}
            </span>

            {/* Separator */}
            <span className="text-[12px] text-sd-text-muted/20 leading-snug shrink-0">|</span>

            {/* Ticker — key swap triggers sd-ticker-in animation */}
            <span
              key={tickerKey}
              className="text-[12px] leading-snug truncate min-w-0 text-sd-text-muted/40 italic sd-ticker-in"
            >
              {tickerText}
            </span>
          </>
        ) : (
          /* Completed — clean summary, no label */
          <span className="text-[12px] leading-snug text-sd-success/60">
            {tickerText}
          </span>
        )}

        {/* Spacer */}
        <span className="flex-1" />

        {/* Chevron */}
        <ChevronRight
          className={`w-3 h-3 text-sd-text-muted/30 shrink-0 transition-transform duration-150 ${
            expanded ? 'rotate-90' : ''
          } opacity-0 group-hover/cap:opacity-100`}
          aria-hidden="true"
        />
      </div>

      {/* Expanded detail — full ghost trace list */}
      <div
        className={`overflow-hidden transition-[max-height,opacity] duration-200 ease-out ${
          expanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="flex flex-col gap-0 border-l border-sd-border/10 ml-[3.25rem]">
          {traces.map((trace) => (
            <GhostTrace
              key={trace.id}
              verb={trace.verb}
              {...(trace.description ? { description: trace.description } : {})}
              {...(trace.input ? { input: trace.input } : {})}
              {...(trace.output ? { output: trace.output } : {})}
              loading={trace.loading}
              success={trace.success}
              error={trace.error}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
